/**
 * How a time-in-stage number is ALLOWED to be written down.
 *
 * This module is small on purpose and lives here rather than in any one app,
 * because the honesty rules are the feature: a seeded (backfilled) entry is an
 * upper bound on when the stage was entered, so its measured age is a LOWER
 * bound on the true age and must read "at least 18 days"; a stage with too few
 * observed transitions has no median and must read "not enough history", never
 * "0 days". A second copy of this logic in Slack or in a report is how the
 * qualifier gets dropped and an estimate starts being read as a measurement.
 */

import type { DealStageOccupancy } from "./occupancy.js";

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export { DAY_MS as STAGE_DAY_MS };

/**
 * "18 days" / "3 hours" / "under an hour" — sales-readable, whole units only.
 *
 * Pipeline age is a days-scale number; a rep reading "18 days 4 hours" gets no
 * more decision value than "18 days", so the smaller unit is dropped rather
 * than concatenated.
 */
export function formatStageDuration(durationMs: number): string {
  const ms = Math.max(0, durationMs);
  if (ms >= DAY_MS) {
    const days = Math.floor(ms / DAY_MS);
    return `${days} ${days === 1 ? "day" : "days"}`;
  }
  if (ms >= HOUR_MS) {
    const hours = Math.floor(ms / HOUR_MS);
    return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  }
  return "under an hour";
}

/** Whole days, floored — the unit every aggregate and every card reports in. */
export function toWholeDays(durationMs: number): number {
  return Math.floor(Math.max(0, durationMs) / DAY_MS);
}

/**
 * The whole phrase for an occupancy, approximation included.
 *
 * A backfilled entry's `changedAt` is an upper bound on when the stage was
 * entered, so the true age is AT LEAST this — say so instead of printing a
 * number that reads as measured.
 */
export function describeStageDuration(entry: DealStageOccupancy): string {
  const duration = formatStageDuration(entry.durationMs);
  return entry.isApproximate ? `at least ${duration}` : duration;
}

/**
 * The same phrase from the two facts alone, for callers that never build a
 * full occupancy (a Slack row, an iOS cell, a tool result).
 */
export function describeDuration(durationMs: number, isApproximate: boolean): string {
  const duration = formatStageDuration(durationMs);
  return isApproximate ? `at least ${duration}` : duration;
}

/** What a UI prints when a stage has no defensible median. NEVER a zero. */
export const NOT_ENOUGH_HISTORY = "not enough history";

/**
 * Render a stage's typical duration, or say we cannot.
 *
 * `medianMs === null` means the stage has fewer than `MIN_STAGE_SAMPLE`
 * observed, completed transitions — there is no median, and printing "0 days"
 * (or silently falling back to the mean of two rows) would present the absence
 * of evidence as evidence of speed.
 */
export function describeTypicalDuration(medianMs: number | null): string {
  return medianMs === null ? NOT_ENOUGH_HISTORY : formatStageDuration(medianMs);
}
